import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLazyQuery } from '@apollo/client';
import { GET_TASK_POOL, QUOTE_FROM_HASH } from '../queries';
import { CoreTaskStatusChoices } from '../gql/graphql';
import { StoreState } from '../store';
import {
  QuoteWizardQuote,
  QuoteWizardAnsweredForm,
} from '../components/QuoteWizard/classes';
import {
  markQuoteAsSubmitted,
  setCurrentQuoteUuid,
  setQuoteSubmissionTaskPoolId,
} from '../store/policystreamStore';
import { extractQuoteStatus } from '../util';
import { usePrevious } from './usePrevious';

const POLLING_INTERVAL_MS = 500;

export interface UsePolicyStreamQuoteSubmissionOptions {
  active?: boolean;
  onComplete?: () => void;
}

export function usePolicyStreamQuoteSubmission({
  active = true,
  onComplete,
}: UsePolicyStreamQuoteSubmissionOptions = {}) {
  const dispatch = useDispatch();
  const taskPoolId = useSelector(
    (state: StoreState) => state.policystreamStore.quoteSubmissionTaskPoolId,
  );
  const formsWithAnswers = useSelector(
    (state: StoreState) => state.policystreamStore.formAnswers,
  );
  const previousTaskPoolId = usePrevious(taskPoolId);

  const [
    getTaskPool,
    {
      data: taskPoolData,
      error: taskPoolError,
      loading: taskPoolLoading,
      stopPolling,
      startPolling,
      called,
    },
  ] = useLazyQuery(GET_TASK_POOL, {
    pollInterval: POLLING_INTERVAL_MS,
  });

  const [
    getQuoteFromHash,
    { data: quoteHashData, error: quoteError, loading: quoteLoading },
  ] = useLazyQuery(QUOTE_FROM_HASH);

  // Start polling only when taskPoolId changes (indicating a new quote submission)
  useEffect(() => {
    if (!active) {
      stopPolling?.();
      return;
    }

    // Only start polling if taskPoolId changed to a new value
    // This ensures we only poll after a new quote submission call has been made
    const hasNewTaskPoolId = taskPoolId && taskPoolId !== previousTaskPoolId;

    if (hasNewTaskPoolId) {
      getTaskPool({
        variables: { taskPoolId },
      });
      startPolling?.(POLLING_INTERVAL_MS);
    } else if (!taskPoolId && previousTaskPoolId) {
      // If taskPoolId was cleared, stop polling
      stopPolling?.();
    }

    // Stop polling on unmount or when active becomes false
    return () => {
      stopPolling?.();
    };
  }, [
    taskPoolId,
    previousTaskPoolId,
    getTaskPool,
    startPolling,
    stopPolling,
    active,
  ]);

  // Stop polling if there's an error
  useEffect(() => {
    const taskPool = taskPoolData?.getTaskPool;
    const hasError = taskPoolError || quoteError || taskPool?.error;

    if (hasError && called) {
      stopPolling?.();
    }
  }, [
    taskPoolError,
    quoteError,
    taskPoolData?.getTaskPool?.error,
    called,
    stopPolling,
  ]);

  // Check if task pool is complete and extract quote_correspondence_hash
  useEffect(() => {
    if (!active) {
      return;
    }

    const taskPool = taskPoolData?.getTaskPool;
    if (!taskPool || !taskPool.tasks || taskPool.tasks.length === 0) {
      return;
    }

    // Check if all tasks and subtasks are complete
    const allTasksComplete = taskPool.tasks.every((task) => {
      if (!task) return false;
      // Task is complete if status is Completed
      return task.status === CoreTaskStatusChoices.Completed;
    });

    if (allTasksComplete && taskPool.additionalInformation) {
      try {
        const additionalInfo = JSON.parse(taskPool.additionalInformation);

        if (!additionalInfo || typeof additionalInfo !== 'object') {
          throw new Error(
            'Invalid additionalInformation format: expected an object',
          );
        }

        const quoteCorrespondenceHash =
          additionalInfo?.quote_correspondence_hash;

        if (
          quoteCorrespondenceHash &&
          typeof quoteCorrespondenceHash === 'string'
        ) {
          // Stop polling once complete
          stopPolling?.();

          // Fetch the quote using the hash
          getQuoteFromHash({
            variables: {
              hash: quoteCorrespondenceHash,
              delete: false,
            },
          });
        }
      } catch (e) {
        // Error handling: stop polling and let the error propagate
        // The error will be caught by the error handling useEffect
        stopPolling?.();
      }
    }
  }, [taskPoolData, getQuoteFromHash, stopPolling, active]);

  // Handle quote data when received from hash
  useEffect(() => {
    if (!active) {
      return;
    }

    if (!quoteHashData?.quoteFromHash) {
      return;
    }

    const quoteData = quoteHashData.quoteFromHash;

    if (!quoteData) {
      return;
    }

    // Extract status from the quote data
    const status = extractQuoteStatus(quoteData);

    // Create QuoteWizardQuote from the response, ensuring status is set
    const quoteWizardQuote = new QuoteWizardQuote({
      ...quoteData,
      status,
    });

    // Convert formsWithAnswers from Record to Array format
    const formsWithAnswersArray: Array<QuoteWizardAnsweredForm> =
      Object.values(formsWithAnswers);

    // Store quote in state
    dispatch(
      markQuoteAsSubmitted({
        formsWithAnswers: formsWithAnswersArray,
        quote: quoteWizardQuote,
      }),
    );

    // Set current quote UUID if available
    if ('uniqueID' in quoteData && quoteData.uniqueID) {
      const uuid =
        typeof quoteData.uniqueID === 'string'
          ? quoteData.uniqueID
          : String(quoteData.uniqueID);
      dispatch(
        setCurrentQuoteUuid({
          uuid,
        }),
      );
    }

    // Call onComplete callback after quote is successfully stored
    if (onComplete) {
      onComplete();
    }

    // Clear the task pool id now that the quote process has completed
    dispatch(setQuoteSubmissionTaskPoolId(null));
  }, [quoteHashData, dispatch, formsWithAnswers, active, onComplete]);

  return {
    taskPool: {
      loading: taskPoolLoading,
      error: taskPoolError,
      data: taskPoolData,
    },
    quote: {
      loading: quoteLoading,
      error: quoteError,
      data: quoteHashData,
    },
  };
}
